import { useId, useState, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";
import { reportIssue } from "../../api/issueReportsApi";
import ErrorNotice from "../../errors/ErrorNotice";
import { ISSUE_REPORT_REFERENCE_PARAMETER } from "../../shared/routing/appPaths";
import { useSubmission } from "../../shared/state/useSubmission";
import { useUnsavedDraft } from "../processAuthoring/draft/useUnsavedDraft";
import { usePaneQuery } from "../../shell/workspace";
import AppButton from "../../ui/buttons/AppButton";
import StatusMessage from "../../ui/feedback/StatusMessage";
import Field from "../../ui/forms/Field";
import { FORM_CONTROL_CLASS } from "../../ui/styles/formStyles";

const ISSUE_TITLE_MAX_LENGTH = 120;

/**
 * `/report-a-problem[?reference=<request>]`: "Report a problem" to DMW's own issue list
 * (FR-VERSION-6, F13). It opens beside the page it is about, so the error stays in view, and
 * no navigation lists it (F13, interpretation recorded there). An error's reference starts the
 * description, so the team can find the request in its logs.
 */
export default function IssueReportPage() {
  const [query] = usePaneQuery();
  const reference = query.get(ISSUE_REPORT_REFERENCE_PARAMETER);
  // Another error's link starts a fresh report.
  return <IssueReportForm key={reference ?? ""} reference={reference} />;
}

function IssueReportForm({ reference }: { reference: string | null }) {
  const { t } = useTranslation();
  const headingId = useId();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState(() => (reference === null ? "" : t("issueReports.bodyWithReference", { reference })));
  const { state, submit } = useSubmission();
  // A page, not a modal: leaving it with a half-written report asks first, through the one draft guard.
  const { markSaved } = useUnsavedDraft({ title, body });
  const pending = state.status === "submitting";
  const sent = state.status === "succeeded";

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit(async () => {
      await reportIssue({ title: title.trim(), body: body.trim() });
      markSaved();
    });
  };

  return (
    <section className="app-card mx-auto my-4 w-full max-w-2xl space-y-4 p-6 shadow-sm" aria-labelledby={headingId}>
      <header>
        <h2 id={headingId} className="app-title text-lg font-semibold">{t("issueReports.title")}</h2>
        <p className="app-text-muted text-sm">{t("issueReports.hint")}</p>
      </header>
      {sent ? (
        <StatusMessage tone="success">{t("issueReports.sent")}</StatusMessage>
      ) : (
        <form onSubmit={handleSubmit}>
          <fieldset disabled={pending} className="space-y-3">
            <Field label={t("issueReports.titleLabel")}>
              {(control) => (
                <input
                  {...control}
                  type="text"
                  value={title}
                  onChange={(event) => { setTitle(event.target.value); }}
                  maxLength={ISSUE_TITLE_MAX_LENGTH}
                  className={FORM_CONTROL_CLASS}
                  placeholder={t("issueReports.titlePlaceholder")}
                  required
                />
              )}
            </Field>
            <Field label={t("issueReports.bodyLabel")} hint={t("issueReports.bodyHelp")}>
              {(control) => (
                <textarea
                  {...control}
                  value={body}
                  onChange={(event) => { setBody(event.target.value); }}
                  rows={7}
                  className={FORM_CONTROL_CLASS}
                  placeholder={t("issueReports.bodyPlaceholder")}
                  required
                />
              )}
            </Field>
            {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
            <div className="flex justify-end">
              <AppButton type="submit" variant="primary" size="sm" disabled={pending || title.trim() === "" || body.trim() === ""}>
                {pending ? t("issueReports.sending") : t("issueReports.submit")}
              </AppButton>
            </div>
          </fieldset>
        </form>
      )}
    </section>
  );
}
